import React, { useCallback, useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import KpiStrip from './shared/KpiStrip';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { PALETTE, axisProps, fmtNum, tooltipStyle } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// Growth vs loyalty: how many of the patients seen were here for the very first time, new
// registrations over the same period, and the patients who kept coming back.
//
// "New" is anchored to a patient's first-ever visit (all time), not the first inside the range -
// otherwise everyone would look new in a short range.

const SERIES_COLUMNS = [
  { key: 'bucket', label: 'Period' },
  { key: 'uniquePatients', label: 'Patients Seen', align: 'right' },
  { key: 'newPatients', label: 'First-Time', align: 'right' },
  { key: 'returningPatients', label: 'Returning', align: 'right' },
  {
    key: 'revisitRate', label: 'Returning %', align: 'right',
    render: (r) => (Number(r.uniquePatients) > 0 ? `${Math.round((Number(r.returningPatients) / Number(r.uniquePatients)) * 100)}%` : '—'),
    csv: (r) => (Number(r.uniquePatients) > 0 ? Math.round((Number(r.returningPatients) / Number(r.uniquePatients)) * 100) : ''),
    sortValue: (r) => (Number(r.uniquePatients) > 0 ? Number(r.returningPatients) / Number(r.uniquePatients) : 0),
  },
];

const FREQUENT_COLUMNS = [
  { key: 'patient', label: 'Patient' },
  { key: 'mrn', label: 'MRN' },
  { key: 'phone', label: 'Phone' },
  { key: 'visits', label: 'Visits', align: 'right' },
  { key: 'firstVisit', label: 'First Visit' },
  { key: 'lastVisit', label: 'Last Visit' },
  { key: 'doctorsSeen', label: 'Doctors Seen', align: 'right' },
];

const NewVsReturningReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('last6Months'));
  const [preset, setPreset] = useState('last6Months');
  const [granularity, setGranularity] = useState('monthly');

  const fetcher = useCallback(
    () => reportsAPI.getNewVsReturning({ from: range.from, to: range.to, granularity }),
    [range, granularity]
  );
  const { data, loading } = useReport(fetcher);

  const totals = useMemo(() => {
    const s = data?.series || [];
    const fresh = s.reduce((sum, b) => sum + Number(b.newPatients), 0);
    const returning = s.reduce((sum, b) => sum + Number(b.returningPatients), 0);
    const regs = (data?.registrations || []).reduce((sum, b) => sum + Number(b.registrations), 0);
    return { fresh, returning, regs };
  }, [data]);

  const kpis = [
    { label: 'First-Time Patients', value: fmtNum(totals.fresh) },
    { label: 'Returning Patients', value: fmtNum(totals.returning) },
    {
      label: 'Returning Share',
      value: totals.fresh + totals.returning > 0
        ? `${Math.round((totals.returning / (totals.fresh + totals.returning)) * 100)}%`
        : '—',
    },
    { label: 'New Registrations', value: fmtNum(totals.regs), sub: 'in the same period' },
    { label: 'Frequent Visitors', value: fmtNum((data?.frequentVisitors || []).length), sub: '2+ visits in range' },
  ];

  return (
    <div className="space-y-5">
      <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />

      <div className="flex items-center gap-3">
        <div className="bg-white rounded-xl shadow p-1 inline-flex">
          {['daily', 'monthly'].map((g) => (
            <button
              key={g}
              onClick={() => setGranularity(g)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${
                granularity === g ? 'bg-navy-800 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
        <div className="ml-auto"><PrintButton /></div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartPanel title="First-time vs returning patients">
          {(data?.series || []).length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-16">No visits in the selected range</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.series} margin={{ left: 8, right: 16 }}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
                <XAxis dataKey="bucket" {...axisProps} />
                <YAxis {...axisProps} allowDecimals={false} />
                <Tooltip {...tooltipStyle} />
                <Legend />
                <Bar dataKey="newPatients" name="First-time" stackId="1" fill={PALETTE[0]} />
                <Bar dataKey="returningPatients" name="Returning" stackId="1" fill={PALETTE[1]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>
        <ChartPanel title="New patient registrations">
          {(data?.registrations || []).length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-16">No registrations in the selected range</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.registrations} margin={{ left: 8, right: 16 }}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
                <XAxis dataKey="bucket" {...axisProps} />
                <YAxis {...axisProps} allowDecimals={false} />
                <Tooltip {...tooltipStyle} />
                <Line type="monotone" dataKey="registrations" stroke={PALETTE[2]} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>
      </div>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'period', value: r.bucket, granularity, from: range.from, to: range.to })}
        title="Patients seen per period"
        columns={SERIES_COLUMNS}
        rows={data?.series || []}
        csvName={`new-vs-returning-${range.from}-to-${range.to}`}
      />
      <ReportTable
        onRowClick={(r) => drill.open({ type: 'patient', mrn: r.mrn })}
        title="Frequent visitors (2+ visits in range)"
        columns={FREQUENT_COLUMNS}
        rows={data?.frequentVisitors || []}
        csvName={`frequent-visitors-${range.from}-to-${range.to}`}
        emptyText="No patient visited more than once in the selected range"
      />

      <ReportPrintLayout
        title="New vs Returning Patients"
        subtitle={`Period: ${range.from} to ${range.to} (${granularity})`}
        kpis={kpis.map(({ label, value }) => ({ label, value }))}
        tables={[
          { title: 'Patients seen per period', columns: SERIES_COLUMNS, rows: data?.series || [] },
          { title: 'Frequent visitors', columns: FREQUENT_COLUMNS, rows: data?.frequentVisitors || [] },
        ]}
      />

      {drill.node}
    </div>
  );
};

export default NewVsReturningReport;
